import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation, usePermissions } from "hooks";
import { translations } from "./translations";
import * as XLSX from "xlsx";

import {
  ListUsersActions,
  ListUsersState,
  PaginateUsersActions,
  PaginateUsersState,
} from "store/ducks/settings/users";

import { SelectedFilterActions, SelectedFilterState } from "store/ducks/filter";

import * as S from "./styles";
import { MainContainer, Paginator, MenuSelector } from "components/shared";
import { GridUsers, FilterUsers } from "components/settings";
import { ImpersonateModal } from "components/settings/Users/ImpersonateModal";

export const ListUsers: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();
  const [query, setQuery] = useState<Record<string, any>>({
    limit: 10,
    page: 1,
  });
  const [openModalImpersonate, setOpenModalImpersonate] = useState(false);
  const [impersonateEmail, setImpersonate] = useState("");

  const { data, loading, pagination } = useSelector<
    RootStateOrAny,
    PaginateUsersState
  >((state) => state.paginateUsers);

  const { loading: loadingExport } = useSelector<RootStateOrAny>(
    (state) => state.listUsers
  ) as ListUsersState;

  const { data: dataFilter } = useSelector<RootStateOrAny>(
    (state) => state.selectedFilter
  ) as SelectedFilterState;

  const exportToXLSX = (dados: any) => {
    if (dados.length > 0) {
      const nameFile = "RELATÓRIO-USUÁRIOS.xlsx";
      const file = XLSX.utils.book_new();
      const wsData = dados.map((user: any) => [
        user.id || "---", 
        user.roles? user.roles[0].name : "---", 
        user.name || "---", 
        user.email || "---",
        user.last_access
        ? new Date(user.last_access).toLocaleString("pt-BR")
        : "---",
    ]);
    
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["ID", "Tipo", "Nome", "E-mail", "Último acesso"], 
        ...wsData,
      ]);
      XLSX.utils.book_append_sheet(file, worksheet, "Dados");
      XLSX.writeFile(file, nameFile);
    } else {
      alert("Não há dados para exportar");
    }
  };

  // Função para buscar dados para exportação
  const getListUsers = useCallback(() => {
    dispatch(
      ListUsersActions.request(
        { all: true, ...dataFilter },
        (dados: any) => {
          exportToXLSX(dados);
        }
      )
    );
  }, [dispatch, dataFilter]);

  const handleSubmitFilter = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        ...value,
        page: 1,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const handlePageChange = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        page: value,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const getData = useCallback(() => {
    dispatch(PaginateUsersActions.request({ ...query, ...dataFilter }));
  }, [dispatch, query, dataFilter]);

  const HandlerModalImpersonate = useCallback((email) => {
    setImpersonate(() => email);
    setOpenModalImpersonate(() => true);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation("configuracoes")} <MenuSelector page="users" />
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          {checkUserPermission("insertUser") && (
            <S.ButtonMini onClick={getListUsers}>
              {loadingExport ? <S.LoadingPage /> : getTranslation("exportar")}
            </S.ButtonMini>
          )}
        <S.HeaderButtons/>
          {checkUserPermission("insertUser") && (
            <S.ButtonMini onClick={() => history.push("/settings/user/new")}>
              {getTranslation("novoUsuario")}
            </S.ButtonMini>
          )}
          <FilterUsers onFilter={handleSubmitFilter} />
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <GridUsers users={data} onImpersonate={HandlerModalImpersonate} />
        <Paginator
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={loading}
        />

        {openModalImpersonate && (
          <ImpersonateModal
            openModal={openModalImpersonate}
            email={impersonateEmail}
            close={() => setOpenModalImpersonate(false)}
          />
        )}
      </S.PageContent>
    </MainContainer>
  );
};
